import { Injectable } from '@angular/core'
import { CmsComponent, Converter, WindowRef } from '@spartacus/core'
import { ConvertedComponentData, FsCatalog, ImageData, LinkData, TextInput, VideoLink } from '../../components/types'
import { ImageUtils } from '../../../shared/utils/image-utils'
import { PDFType } from '../../enums/pdf-type'
import { ImageViewport } from '../../enums/image-viewports'
import { CmsDOMInputParser } from '../../../shared/utils/cms-dom-input-parser'
import { v4 as uuidv4 } from 'uuid'
import { CmsLinkType } from '../../enums/cms-link-type'
import { ObjectUtils } from '../../../shared/utils/object-utils'

type OmitConvertedComponentData<T> = Omit<T, keyof ConvertedComponentData>

@Injectable({ providedIn: 'root' })
export abstract class CmsConverterService<T extends ConvertedComponentData> implements Converter<CmsComponent, T> {

  constructor (protected cmsParser: CmsDOMInputParser,
               protected winRef: WindowRef) {
  }

  static getImageSize (image: any, viewport: ImageViewport, size: number): string {
    const picture = image?.value?.[0]?.value?.target ?? undefined
    return picture ? `${picture.baseUrl}/-Timage_${viewport}-B${size}` : ''
  }

  static mapImage (image: any, lazyLoad: boolean, altText?: string, title?: string): ImageData {
    const picture = ObjectUtils.getClone(image?.value?.[0]?.value?.target) ?? {}
    const urlParams = ImageUtils.getImageRatioParameters(ImageUtils.getRatioByString(picture?.ratio))
    if (picture?.baseUrl) {
      picture.baseUrl = picture?.mime === 'image/gif'
        ? picture.baseUrl?.replace('/image/', '/rendition/')
        : picture.baseUrl?.replace('/rendition/', '/image/')
    }
    const imageData = {
      altText: altText || picture?.altTag || '',
      title: title || picture?.altTag || '',
      mime: picture?.mime ?? '',
      lazyLoad: lazyLoad ?? true,
      original: picture?.baseUrl ?? '',
      width: picture?.width ?? '',
      height: picture?.height ?? '',
      xl: picture?.baseUrl ? `${picture.baseUrl}/${urlParams?.xl ?? ''}` : '',
      lg: picture?.baseUrl ? `${picture.baseUrl}/${urlParams?.lg ?? ''}` : '',
      md: picture?.baseUrl ? `${picture.baseUrl}/${urlParams?.md ?? ''}` : '',
      sm: picture?.baseUrl ? `${picture.baseUrl}/${urlParams?.sm ?? ''}` : '',
      xs: picture?.baseUrl ? `${picture.baseUrl}/${urlParams?.xs ?? ''}` : '',
      xl2x: picture?.baseUrl ? `${picture.baseUrl}/${urlParams?.xl2x ?? ''}` : '',
      lg2x: picture?.baseUrl ? `${picture.baseUrl}/${urlParams?.lg2x ?? ''}` : '',
      md2x: picture?.baseUrl ? `${picture.baseUrl}/${urlParams?.md2x ?? ''}` : '',
      sm2x: picture?.baseUrl ? `${picture.baseUrl}/${urlParams?.sm2x ?? ''}` : '',
      xs2x: picture?.baseUrl ? `${picture.baseUrl}/${urlParams?.xs2x ?? ''}` : '',
    } as any
    switch (picture?.mime) {
      case 'image/svg+xml': {
        imageData.original = picture?.baseUrl ? picture.baseUrl.replace('/image/', '/asset/') : ''
        imageData.xl2x = imageData.xl2x.replace('/image/', '/asset/')
        imageData.xl = imageData.xl.replace('/image/', '/asset/')
        imageData.lg2x = imageData.lg2x.replace('/image/', '/asset/')
        imageData.lg = imageData.lg.replace('/image/', '/asset/')
        imageData.md2x = imageData.md2x.replace('/image/', '/asset/')
        imageData.md = imageData.md.replace('/image/', '/asset/')
        imageData.sm2x = imageData.sm2x.replace('/image/', '/asset/')
        imageData.sm = imageData.sm.replace('/image/', '/asset/')
        imageData.xs2x = imageData.xs2x.replace('/image/', '/asset/')
        imageData.xs = imageData.xs.replace('/image/', '/asset/')
        imageData.original += imageData.original.indexOf('content-disposition') > -1 ? '' : '?content-disposition=inline'
        imageData.xl2x += imageData.original.indexOf('content-disposition') > -1 ? '' : '?content-disposition=inline'
        imageData.xl += imageData.original.indexOf('content-disposition') > -1 ? '' : '?content-disposition=inline'
        imageData.lg2x += imageData.original.indexOf('content-disposition') > -1 ? '' : '?content-disposition=inline'
        imageData.lg += imageData.original.indexOf('content-disposition') > -1 ? '' : '?content-disposition=inline'
        imageData.md2x += imageData.original.indexOf('content-disposition') > -1 ? '' : '?content-disposition=inline'
        imageData.md += imageData.original.indexOf('content-disposition') > -1 ? '' : '?content-disposition=inline'
        imageData.sm2x += imageData.original.indexOf('content-disposition') > -1 ? '' : '?content-disposition=inline'
        imageData.sm += imageData.original.indexOf('content-disposition') > -1 ? '' : '?content-disposition=inline'
        imageData.xs2x += imageData.original.indexOf('content-disposition') > -1 ? '' : '?content-disposition=inline'
        imageData.xs += imageData.original.indexOf('content-disposition') > -1 ? '' : '?content-disposition=inline'
        break
      }
      case 'image/png': {
        imageData.original = picture?.baseUrl ? `${picture.baseUrl}/-S2500x1250-FPNG` : ''
        break
      }
      default: {
        imageData.original = picture?.baseUrl ? `${picture.baseUrl}/-S2500x1250` : ''
        break
      }
    }
    return imageData
  }

  static getPDFImage (pdf: any): any {
    let imageBaseUrl
    const assetType = pdf.assetType
    switch (assetType) {
      case PDFType.BROCHURE: {
        imageBaseUrl = 'assets/images/pdfs/brochure.jpg'
        break
      }
      case PDFType.DATASHEET: {
        imageBaseUrl = 'assets/images/pdfs/data_sheet.jpg'
        break
      }
      case PDFType.REFERENCE_LIST: {
        imageBaseUrl = 'assets/images/pdfs/reference_list.jpg'
        break
      }
      case PDFType.SCIENTIFIC_POSTER: {
        imageBaseUrl = 'assets/images/pdfs/scientific_poster.jpg'
        break
      }
      case PDFType.APP_NOTE: {
        imageBaseUrl = 'assets/images/pdfs/app_note.jpg'
        break
      }
      case PDFType.DOWNLOAD:
      default: {
        imageBaseUrl = 'assets/images/pdfs/download.jpg'
        break
      }
    }
    return {
      baseUrl: imageBaseUrl,
      xl: imageBaseUrl,
      xl2x: imageBaseUrl,
      lg: imageBaseUrl,
      lg2x: imageBaseUrl,
      md: imageBaseUrl,
      md2x: imageBaseUrl,
      sm: imageBaseUrl,
      sm2x: imageBaseUrl,
      xs: imageBaseUrl,
      xs2s: imageBaseUrl,
      ratio: '2_1'
    }
  }

  convert (source: CmsComponent, target?: T): T {
    const preConvertedSource = this.preConvert(ObjectUtils.getClone(source))
    return Object.assign({}, target, {
      ...this.convertHead(preConvertedSource),
      ...this.convertData(preConvertedSource),
    })
  }

  protected abstract convertData (source: CmsComponent): OmitConvertedComponentData<T>

  protected convertHead (source: CmsComponent): ConvertedComponentData {
    const formData = source?.otherProperties?.formData ?? {}
    const head: ConvertedComponentData = {
      uid: source?.uid ?? '',
      previewId: source?.otherProperties?.previewId ?? '',
      visibility: {
        hide: formData.st_hide_component?.value ?? false,
        hideBefore: formData.st_hide_before_date?.value ?? '',
        hideAfter: formData.st_hide_after_date?.value ?? ''
      },
      uuid: uuidv4()
    }
    if (this.winRef.isBrowser() && typeof this.winRef.nativeWindow !== 'undefined' && this.winRef.nativeWindow.Cypress) {
      head.source = source
    }
    return head
  }

  private preConvert (obj: any): any {
    if (!obj?.otherProperties?.formData) {
      return obj
    }
    const formData = obj.otherProperties.formData
    Object.keys(formData).forEach((key: string) => {
        const el = formData[key]
        if (el && el['fsType']) {
          try {
            formData[key] = this.convertByFsType(el)
          } catch {
          }
        }
      }
    )
    obj.otherProperties.formData = formData
    return obj
  }

  private convertByFsType (obj: any): any {
    switch (this.getFSType(obj)) {
      case 'CMS_INPUT_DOM':
      case 'CMS_INPUT_TEXT':
        return this.convertTextInput(obj)
      case 'FS_CATALOG':
        return this.convertFsCatalog(obj)
      case 'CMS_INPUT_LINK':
        return this.convertInputLink(obj.value)
      case 'FS_INDEX':
        return this.convertFsIndex(obj)
      default:
        return obj
    }
  }

  private getFSType (obj: any): string {
    return obj['fsType']
  }

  private convertFsIndex (input: FsIndex): any {
    if (input?.dapType?.includes('ContentConnectSAPCommerceCloud')) {
      input.value = input?.value?.map(item => item?.identifier?.split('/')?.[1] ?? '') as any[]
      return input
    }
    if (input?.dapType?.includes('Canto')) {
      if (!input.value?.[0]?.value?.target?.baseUrl) {
        return input
      }
      (input as any).value[0].value.target.url = input.value[0].value.target.baseUrl.replace('rendition', 'image')
      if (input.value[0].value.target.mime === 'application/pdf'
        || input.value[0].value.target.title?.toLowerCase()?.includes('pdf')
        || input?.name?.includes('pdf')) {
        (input as any).value[0].value.target.image = CmsConverterService.getPDFImage(input.value[0].value.target)
      }
      return input
    }
    if (input?.dapType?.includes('video')) {
      const video = input.value?.[0]?.identifier ?? ''
      return video?.substring(video.lastIndexOf('/') + 1) ?? ''
    }
    if (input?.dapType?.includes('TagDataAccessPlugin')) {
      return input?.value?.map((value: any) => value?.value?.target ?? {})
    }
    if (input?.dapType?.includes('DatasetDataAccessPlugin') && input?.name?.includes('st_csod_categories')) {
      input?.value?.map((value: any) => {
        value.value.target.dataset = {
          categoryId: value.value?.target?.dataset?.otherProperties?.formData?.st_csod_category_id?.value,
          categoryTitle: value.value?.target?.dataset?.otherProperties?.formData?.st_csod_category_title?.value
        }
        return value
      })
      return input
    }
    return null
  }

  private convertTextInput (input: ValueNameData<string>): TextInput {
    if (input?.value?.length) {
      input.value = this.cmsParser.parse(input.value)
    }
    return input as TextInput
  }

  private convertFsCatalog (input: ValueNameData<Array<any>>): FsCatalog {
    return input as any
  }

  private convertInputLink (inputLink: any = {}): LinkData {
    let link = {} as LinkData
    const formData = inputLink?.formData ?? {}
    link.text = formData?.lt_text?.value?.length ? this.cmsParser.parse(formData.lt_text.value) : ''
    link.tooltip = formData?.lt_tooltip_text?.value ?? ''
    link.uid = inputLink?.template?.uid || CmsLinkType.CONTENT_LINK
    link.linkStyle = link.uid?.endsWith('_button') ? 'button' : 'normal'
    link.highlight = formData?.lt_highlight?.value?.identifier
      ? formData?.lt_highlight?.value?.identifier === 'true'
      : formData?.st_highlighting?.value?.identifier === 'true'
    link.url = ''
    switch (link.uid) {
      case 'document_link':
      case 'document_link_button': {
        const pdf = formData.lt_reference?.value?.[0]?.value?.target ?? {}
        link.mime = pdf?.mime ?? ''
        link.fileSize = pdf?.size ?? 0
        link.assetType = pdf.assetType ?? ''
        link.assetName = pdf.displayName ?? ''
        link.url = pdf.baseUrl ?? ''
        link.target = inputLink.formData?.lt_target?.value?.identifier ?? '_blank'
        link.image = formData.lt_reference?.image ?? CmsConverterService.getPDFImage(pdf)
        break
      }
      case 'video_link': {
        const video = formData?.st_video?.value?.[0]?.identifier ?? ''
        const videoId = video?.substring(video.lastIndexOf('/') + 1) ?? ''
        const l = link as VideoLink
        l.overlayCaption = formData.caption_overlay_text ?? ''
        l.overlayDescription = formData.description_overlay_text ?? ''
        l.videoId = videoId
        link = l
        break
      }
      case 'content_link':
      case 'content_link_button': {
        link.anchor = formData?.lt_anchor?.value ?? ''
        link.target = formData?.lt_target?.value?.identifier ?? '_self'
        link.pageUid = formData?.lt_page?.value?.identifier ?? ''
        break
      }
      case 'url':
      case 'url_button': {
        link.anchor = formData?.lt_anchor?.value ?? ''
        link.url = formData?.lt_url?.value ?? ''
        link.target = formData?.lt_target?.value?.identifier ?? '_self'
        break
      }
      case 'anchor':
      case 'anchor_button': {
        link.anchor = formData?.lt_anchor?.value ?? ''
        link.target = formData?.lt_target?.value?.identifier ?? '_self'
        break
      }
      case 'related_product_link': {
        link.target = formData.lt_target?.value?.identifier ?? ''
        link.anchor = formData.lt_anchor?.value ?? ''
        link.pageUid = formData?.lt_page?.value?.identifier ?? ''
        break
      }
      case 'related_handbook_article_link': {
        link.pageUid = formData?.lt_page?.value?.identifier ?? ''
        link.anchor = formData.lt_anchor?.value
        break
      }
      case 'product_link':
      case 'product_link_button': {
        link.pageUid = formData.lt_page?.value?.identifier ?? ''
        link.target = formData.lt_target?.value?.identifier ?? ''
        link.productCode = formData?.lt_product?.value?.[0]?.identifier?.split('/')?.pop() ?? ''
        break
      }
      case 'print': {
        link.url = 'window.print()'
        break
      }
      case 'email_link':
      case 'email_link_button': {
        link.url = 'mailto:' + formData?.lt_url?.value ?? ''
        break
      }
      default:
        break
    }
    return link
  }
}

export const defaultTextInput = { name: '', value: '' }
export const defaultBooleanInput = { name: '', value: false }
export const defaultFSCatalog = { name: '', value: [] }
export const defaultFSFacet = { category: '', name: '', source: '', sourceId: '', query: '' }
export const defaultLinkData = {}

interface ValueNameData<ValueType> {
  name?: string;
  value?: ValueType;
}

interface FsIndex {
  dapType: string;
  fsType: string;
  name: string;
  value: FsIndexValue[];
}

export interface FsIndexValue {
  fsType: string;
  identifier: string;
  value?: {
    target: {
      baseUrl: string;
      description: string;
      title: string;
      width: string;
      height: string;
      ratio: string;
      assetType: string;
      mime: string;
      dataset?: {};
    };
  };
}
